import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';
import './admin.css';

const PopupManager = () => {
  const [popupImages, setPopupImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const API_BASE_URL = 'https://imageapi.magicmoonstudios.com';

  // Fetch popup images from Firebase
  const fetchPopupImages = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'popupImages'));
      const images = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPopupImages(images);
    } catch (error) {
      console.error('Error fetching popup images:', error);
      toast.error('Failed to load popup images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopupImages();
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Upload image to API and save URL to Firebase
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.warning('Please select an image first');
      return;
    }

    try {
      setUploading(true);

      // Upload to image API
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('project', 'popup');
      formData.append('type', 'landing');

      const uploadResponse = await fetch(`${API_BASE_URL}/upload.php`, {
        method: 'POST',
        body: formData
      });

      const uploadResult = await uploadResponse.json();

      if (uploadResult.success) {
        // Save to Firebase
        await addDoc(collection(db, 'popupImages'), {
          url: uploadResult.url,
          uploadedAt: new Date().toISOString(),
          active: true
        });

        toast.success('Image uploaded successfully!');
        setSelectedFile(null);
        setPreviewUrl('');
        fetchPopupImages();
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Delete image from API and Firebase
  const handleDelete = async (image) => {
    if (!window.confirm('Are you sure you want to delete this popup image?')) {
      return;
    }

    try {
      setLoading(true);

      // Delete from API
      const formData = new FormData();
      formData.append('url', image.url);

      const deleteResponse = await fetch(`${API_BASE_URL}/delete.php`, {
        method: 'POST',
        body: formData
      });

      const deleteResult = await deleteResponse.json();

      if (deleteResult.success) {
        // Delete from Firebase
        await deleteDoc(doc(db, 'popupImages', image.id));
        toast.success('Image deleted successfully!');
        fetchPopupImages();
      } else {
        throw new Error(deleteResult.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Replace existing image with new one
  const handleReplace = async (oldImage) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setUploading(true);

        // Upload new image
        const formData = new FormData();
        formData.append('image', file);
        formData.append('project', 'popup');
        formData.append('type', 'landing');

        const uploadResponse = await fetch(`${API_BASE_URL}/upload.php`, {
          method: 'POST',
          body: formData
        });

        const uploadResult = await uploadResponse.json();

        if (uploadResult.success) {
          // Delete old image from API
          const deleteFormData = new FormData();
          deleteFormData.append('url', oldImage.url);

          await fetch(`${API_BASE_URL}/delete.php`, {
            method: 'POST',
            body: deleteFormData
          });

          // Update Firebase with new URL
          await updateDoc(doc(db, 'popupImages', oldImage.id), {
            url: uploadResult.url,
            uploadedAt: new Date().toISOString()
          });

          toast.success('Image replaced successfully!');
          fetchPopupImages();
        } else {
          throw new Error(uploadResult.error || 'Upload failed');
        }
      } catch (error) {
        console.error('Error replacing image:', error);
        toast.error('Failed to replace image: ' + error.message);
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  // Toggle image active status
  const toggleActive = async (image) => {
    try {
      await updateDoc(doc(db, 'popupImages', image.id), {
        active: !image.active
      });
      toast.success(`Image ${!image.active ? 'activated' : 'deactivated'}`);
      fetchPopupImages();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  return (
    <Dashboard>
      <div className="popup-manager">
        <div className="manager-header">
          <h2>Landing Page Popup Manager</h2>
          <p>Upload and manage popup images for the landing page</p>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <h3>Upload New Popup Image</h3>
          <div className="upload-container">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              className="file-input"
            />
            
            {previewUrl && (
              <div className="preview-container">
                <img src={previewUrl} alt="Preview" className="preview-image" />
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="upload-btn"
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>

        {/* Images List */}
        <div className="images-section">
          <h3>Current Popup Images ({popupImages.length})</h3>
          
          {loading ? (
            <div className="loading">Loading images...</div>
          ) : popupImages.length === 0 ? (
            <div className="no-images">No popup images uploaded yet</div>
          ) : (
            <div className="images-grid">
              {popupImages.map((image) => (
                <div key={image.id} className={`image-card ${!image.active ? 'inactive' : ''}`}>
                  <div className="image-preview">
                    <img src={image.url} alt="Popup" />
                    {!image.active && <div className="inactive-overlay">Inactive</div>}
                  </div>
                  
                  <div className="image-info">
                    <p className="upload-date">
                      Uploaded: {new Date(image.uploadedAt).toLocaleDateString()}
                    </p>
                    <p className="image-url">{image.url}</p>
                  </div>

                  <div className="image-actions">
                    <button
                      onClick={() => toggleActive(image)}
                      className={`toggle-btn ${image.active ? 'active' : ''}`}
                      title={image.active ? 'Deactivate' : 'Activate'}
                    >
                      {image.active ? '👁️ Active' : '🚫 Inactive'}
                    </button>
                    
                    <button
                      onClick={() => handleReplace(image)}
                      disabled={uploading}
                      className="replace-btn"
                      title="Replace image"
                    >
                      🔄 Replace
                    </button>
                    
                    <button
                      onClick={() => handleDelete(image)}
                      disabled={loading}
                      className="delete-btn"
                      title="Delete image"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dashboard>
  );
};

export default PopupManager;
